import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-402 Culiacán (GDD §10.6, §10.2, issue #132): the afternoon in the city, written by hand with the format of
// §15.6. 600 u at 6.75 u/s (world 4, level 2): takeoff, warm-up (20 %), tension (30 %) up to CP-402-A at x 300,
// respite (10 %), peak (40 %) and the clandestine airstrip. Gap columns every 7 u with the 3.2 u gap of world 4
// (§5.4), the gap centre never moving more than 4.0 u, alternating the `w4-culiacan-block` and `w4-culiacan-chapel`
// skins (#126). The tequila-style tunnels (EN-43, #129) shoot barrels from the ground: the first one comes alone in
// the warm-up, far from the next column. The peak adds the law (EN-02) and a hitman plane (EN-03). The world 4
// escapular hides on a risky route over two tunnels. The miniboss event `mini-guero` (El Güero Palmera) is data
// only for now (@gdd-pending). An armor before the checkpoint and a bribe in the respite. All x are local.

const block = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.2, skin: 'w4-culiacan-block' });
const chapel = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.2, skin: 'w4-culiacan-chapel' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
const scapular = (x: number, y: number): PickupDef => ({ x, y, type: 'scapular' });
/** A tunnel trapdoor (EN-43) at the ground at x. */
const tunnel = (x: number): EnemyDef => ({ x, y: 1, type: 'EN-43' });
/** A helicopter of the law (EN-02), hovering high. */
const law = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });
/** A hitman plane (EN-03), announced with INTERCEPT. */
const hitman = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-03' });

const cols = (start: number, count: number, skins: readonly ('block' | 'chapel')[]): ColumnDef[] =>
  Array.from({ length: count }, (_, i) => {
    const y = 6.0 + (i % 3) * 0.5;
    return (skins[i % skins.length] === 'block' ? block : chapel)(start + i * 7.0, y);
  });

const takeoff: SegmentDef = { id: 'w4-culiacan-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w4-culiacan-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 55,
  // The first tunnel comes alone: no column within 6 u of its chimney (GDD §10.6, #132).
  columns: [block(10.0, 6.0), chapel(17.0, 6.5), block(24.0, 6.0), chapel(45.0, 6.0)],
  enemies: [tunnel(34.0)],
  pickups: [
    dollar(13.25, 6.0),
    dollar(14.25, 6.3),
    dollar(15.25, 6.0),
    dollar(38.25, 6.25),
    dollar(39.25, 6.55),
    dollar(40.25, 6.25),
  ],
};

const warmup02: SegmentDef = {
  id: 'w4-culiacan-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 55,
  columns: [...cols(4.0, 7, ['chapel', 'block'])],
  enemies: [],
  pickups: [
    dollar(14.25, 6.0),
    dollar(15.25, 6.3),
    dollar(16.25, 6.0),
    dollar(32.25, 6.5),
    dollar(33.25, 6.8),
    dollar(34.25, 6.5),
  ],
};

const tension01: SegmentDef = {
  id: 'w4-culiacan-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [block(5.0, 6.0), chapel(12.0, 6.5), block(19.0, 6.0), chapel(26.0, 6.0), block(33.0, 6.5), chapel(40.0, 6.0), block(47.0, 6.5)],
  enemies: [tunnel(22.0)],
  pickups: [
    dollar(15.25, 6.25),
    dollar(16.25, 6.55),
    dollar(17.25, 6.25),
    dollar(43.25, 6.25),
    dollar(44.25, 6.55),
    dollar(45.25, 6.25),
  ],
};

const tension02: SegmentDef = {
  id: 'w4-culiacan-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [chapel(5.0, 6.0), block(12.0, 6.0), chapel(19.0, 6.5), block(26.0, 6.0), chapel(33.0, 6.0), block(40.0, 6.5), chapel(47.0, 6.0)],
  enemies: [tunnel(16.0), tunnel(38.0)],
  pickups: [
    dollar(15.25, 6.25),
    dollar(16.25, 6.55),
    dollar(17.25, 6.25),
    dollar(36.25, 6.25),
    dollar(37.25, 6.55),
    dollar(38.25, 6.25),
    armor(9.75, 6.25),
  ],
};

const tension03: SegmentDef = {
  id: 'w4-culiacan-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [...cols(5.0, 7, ['block', 'chapel'])],
  enemies: [],
  pickups: [
    dollar(15.25, 6.5),
    dollar(16.25, 6.8),
    dollar(17.25, 6.5),
    dollar(37.25, 6.25),
    dollar(38.25, 6.55),
    dollar(39.25, 6.25),
  ],
};

const respite01: SegmentDef = {
  id: 'w4-culiacan-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 60,
  columns: [chapel(15.0, 6.0), block(38.0, 6.5)],
  enemies: [],
  pickups: [
    dollar(20.25, 6.0),
    dollar(21.25, 6.3),
    dollar(22.25, 6.0),
    dollar(43.25, 6.5),
    dollar(44.25, 6.8),
    dollar(45.25, 6.5),
    bribe(30.25, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w4-culiacan-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [...cols(11.0, 8, ['block', 'chapel'])],
  // The escapular hides over two tunnels on a risky route (GDD §7, #132).
  enemies: [tunnel(26.0), tunnel(44.0), law(56.0, 11.0)],
  pickups: [
    dollar(20.25, 6.25),
    dollar(21.25, 6.55),
    dollar(22.25, 6.25),
    dollar(49.25, 6.25),
    dollar(50.25, 6.55),
    dollar(51.25, 6.25),
    scapular(35.0, 10.6),
  ],
};

const peak02: SegmentDef = {
  id: 'w4-culiacan-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [...cols(11.0, 8, ['chapel', 'block'])],
  enemies: [tunnel(30.0), hitman(48.0, 11.0)],
  pickups: [
    dollar(19.25, 6.25),
    dollar(20.25, 6.55),
    dollar(21.25, 6.25),
    dollar(46.25, 6.25),
    dollar(47.25, 6.55),
    dollar(48.25, 6.25),
  ],
};

const peak03: SegmentDef = {
  id: 'w4-culiacan-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [...cols(11.0, 8, ['block', 'chapel'])],
  enemies: [tunnel(24.0), law(40.0, 11.0)],
  pickups: [
    dollar(18.25, 6.25),
    dollar(19.25, 6.55),
    dollar(20.25, 6.25),
    dollar(46.25, 6.25),
    dollar(47.25, 6.55),
    dollar(48.25, 6.25),
  ],
};

const airstrip: SegmentDef = { id: 'w4-culiacan-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_402_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_402: LevelDef = {
  id: '4-2',
  op: 'OP-402',
  name: 'Culiacán',
  world: 4,
  scrollSpeed: 6.75,
  timeOfDay: 'afternoon',
  segments: OP_402_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-402-A', x: 300 }],
  ending: 'airstrip',
  // El Güero Palmera crosses three times in his golden helicopter from x 360 to 540 (GDD §10.6, #132): data only;
  // the runner that shows the radio line and the passes comes later (@gdd-pending).
  events: [{ id: 'mini-guero', fromX: 360, toX: 540 }],
};
